import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { Modal, ModalActions, Button, CardRadio } from '../../components/shared';
import { tCalendar, getCalendarLang, subscribeCalendarLang } from '../../lib/calendar-i18n';
import type { EventScope } from '../../api/calendar';
import './RecurrenceScopeDialog.css';

/**
 * 반복 일정 범위(scope) 선택 — 삭제·이동 전에 "어디까지 적용할지" 를 묻는다.
 *
 * 왜 필요한가: backend 는 예전부터 `?scope=this|future|all` 을 받았는데(V2.2.1 RRULE)
 * 프론트가 이를 보내지 않아 언제나 scope=all 로 나갔다. 반복 중 하루만 지우려 해도
 * 시리즈 전체가 soft-delete 되는 데이터 손실 경로였다.
 *
 * 왜 LayerPopup 을 안 쓰는가: layerConfirm 은 예/아니오 2지선다다. 3지선다를 넣으려면
 * 전역 팝업의 Kind 유니온과 렌더를 고쳐야 하는데, 그건 앱 전체가 쓰는 공용 인프라라
 * 회귀 면적이 크다. 대신 LayerPopup 이 검증한 구조(zustand 큐 + imperative API + portal)
 * 를 그대로 본떠 캘린더 안에 따로 둔다 — 호출부(async 핸들러)는 await 한 줄로 같다.
 *
 * 사용:
 *   const scope = await askRecurrenceScope({ action: 'delete', title: e.title });
 *   if (!scope) return;                    // 취소·ESC·배경 클릭
 *   await deleteEvent(e.id, { scope, occurrenceAt: e.occurrenceAt });
 *
 * 호스트는 CalendarPage 가 1회 마운트한다(<RecurrenceScopeHost />).
 */

export type RecurrenceAction = 'delete' | 'move' | 'edit';

export interface AskRecurrenceScopeOpts {
  action: RecurrenceAction;
  /** 일정 제목 — 무엇을 지우는지 문장에 넣는다. */
  title: string;
  /** 고를 수 없는 범위와 그 사유. 예: 앞당기는 future 이동(서버가 400 으로 막음). */
  disabled?: Partial<Record<EventScope, string>>;
  /**
   * ⭐ 2026-10-14 (반복 규칙 1단계 B) — 고를 수 있는 범위 아래에 덧붙일 한 줄(예: 「이후 일정은 매주 수·금으로
   *   반복됩니다.」). 잠긴 범위에는 사유가 우선이라 보이지 않는다.
   */
  notes?: Partial<Record<EventScope, string>>;
  /**
   * ⭐ 2026-10-14 (반복 규칙 F) — 범위 아래 기본 안내를 이 글로 «바꿔» 보인다. 예: 날짜·시간·반복을 바꾼 수정에서
   *   「전체 일정」 아래 「지난 일정은 그대로 두고 오늘 이후 일정부터 바뀝니다.」(기본 「지난 일정까지 반복 전체가 바뀝니다.」는
   *   그 경우 틀린 말이다). 잠긴 범위에는 사유가 우선이다.
   */
  hints?: Partial<Record<EventScope, string>>;
}

interface DialogItem extends AskRecurrenceScopeOpts {
  id: number;
  resolve: (v: EventScope | null) => void;
}

interface ScopeStore {
  queue: DialogItem[];
  push: (item: DialogItem) => void;
  resolveTop: (v: EventScope | null) => void;
}

const useScopeStore = create<ScopeStore>((set, get) => ({
  queue: [],
  push: (item) => set((s) => ({ queue: [...s.queue, item] })),
  resolveTop: (v) => {
    const top = get().queue[0];
    if (!top) return;
    top.resolve(v);
    set((s) => ({ queue: s.queue.slice(1) }));
  },
}));

let _seq = 1;

/** 반복 범위를 묻는다. 확인=고른 scope, 취소·ESC·배경=null. */
export function askRecurrenceScope(opts: AskRecurrenceScopeOpts): Promise<EventScope | null> {
  return new Promise<EventScope | null>((resolve) => {
    useScopeStore.getState().push({ id: _seq++, resolve, ...opts });
  });
}

/** 테스트 격리 — 남은 큐를 취소로 비운다. */
export function _resetRecurrenceScopeQueueForTests(): void {
  const { queue, resolveTop } = useScopeStore.getState();
  for (let i = 0; i < queue.length; i++) resolveTop(null);
}

const SCOPES: EventScope[] = ['this', 'future', 'all'];

/**
 * 동작별로 보여줄 범위.
 *
 * ## ⭐⭐ 지금 — **세 동작 모두 셋 다 보인다** (2026-10-06 사용자 결정으로 «되살렸다»)
 *
 * ```
 * 2026-09-14  수정·이동에서 '전체 일정' 을 뺐다        (아래 「지난 이력」 ①)
 * 2026-10-04  #2158 로 되살렸다 → 다음 날 운영에서 터졌다 (아래 ②)
 * 2026-10-05  revert ＋ 「이후 모두」까지 빼 `['this']` 하나가 됐다 (아래 ③)
 * ⭐ 2026-10-06  ★ 다시 «셋 다»로 되살렸다 — ⇒ 까닭은 ★ 아래 여섯(ⓐ~ⓕ)＋일곱째가 «고쳐졌기» 때문
 * ```
 *
 * ### ★ 되살림의 근거 — 고쳐진 결함들. ⚠ 「고쳐졌다」가 아니라 «재서» 적는다
 *
 * ```
 * ⓐ BYDAY 하루 밀림        rewriteBydayFromDtstart       ✅ 28차 운영 확인
 * ⓑ 빈 껍데기 master       futureSplitLeavesEmptyMaster  ✅ 29차
 * ⓒ 삭제 길 통째 삭제      deletedWholeSeries            ✅ 29차 운영 확인
 * ⓓ 예외 409              그 코드를 «내는» 곳 0건         ✅ ⓧ(#2204)
 * ⓔ 겹침 409              findOccurrenceCollision       ✅ #2201
 * ⓕ 요일 둘 400           isBydayAmbiguousForDtstart    ✅ 29차 운영 확인
 * ★ ⓖ ⭐ «이 파일이 좁혀진 까닭» 그 자체 — 「보낸 startAt 을 master.startAt 에 그대로 넣어
 *      앞 회차가 사라진다」  ⇒ ✅ 고쳐졌다. 관제의 여섯에 ⓖ 는 «없었다» — 내가 따로 쟀다.
 * ```
 *
 * ⭐ **ⓖ 를 제품 코드로 다시 잰 값** (`expandOccurrences`·`planSeriesShift` 를 불러서 ·
 * `FREQ=WEEKLY;BYDAY=MO;COUNT=5` · master 10/05 01:00Z · 사용자가 «셋째» 회차를 고름):
 * ```
 * ⓪ 옛 길(보낸 startAt 을 그대로)  잃은 회차 ⛔ 2 (10/05·10/12) · 마지막 11/02 → 11/16
 *    ⇒ ★ 머리말이 적어 둔 「앞 2회 소멸 ＋ 종료 2주 밀림」과 ⭐ «글자까지 같은» 모습이다
 * ① 오늘 길 · 제목만        잃은 회차 ✅ 0   (movesTime=false ⇒ master.startAt 을 «안 건드린다»)
 * ① 오늘 길 · 하루 뒤로     잃은 회차 ✅ 0   BYDAY MO → TU 로 «밀렸다»(ⓐ)
 * ① 오늘 길 · 30분 당김     잃은 회차 ✅ 0
 * ```
 * ⚠⚠ **판정은 회차 «수»가 아니라 «날짜 집합»으로 해야 한다.** `COUNT=` 규칙은 다시 기준을 잡아도
 *   수가 5 그대로이고 창만 통째로 미끄러진다 ⇒ 수만 보면 ⓪ 이 «통과»한다(내가 처음 그렇게 쟀다).
 *   delta 로 민 경우는 「원래 회차 ＋ delta」와 맞댄다 — 그래야 「밀렸다」와 「잃었다」가 갈린다.
 *
 * ### ⇒ 아래 「세 번째 되살림을 막는 조건」 셋을 «하나씩» 확인했다
 * ```
 * ① BYDAY·BYMONTHDAY·BYSETPOS 를 어떻게 다룰지 정한다
 *    ⇒ BYDAY ✅ ⓐ 가 민다. BYMONTHDAY·BYSETPOS·BYMONTH 는 ★ «만드는 곳이 0» 이다 —
 *      buildRecurrenceRule 이 내는 조각은 FREQ·INTERVAL·COUNT·UNTIL·BYDAY 다섯뿐이고,
 *      주석을 덮고 세면 그 세 이름이 «코드»에 있는 곳은 parseRecurrenceRule.test.ts 하나(읽기)다.
 *      ⇒ 즉 ★ 도달 불가로 닫혔다(만들 수 없는 것은 깨질 수 없다).
 *    ⚠ 남은 것: ⛔ «운영 DB 에 그런 행이 있나»는 내가 못 쟀다 — 관제에게 조회 꼴로 넘겼다.
 *    ⇒ ⭐ 그 뒤로 더 쟀고 사용자가 정했다 — 바로 아래 「BY* 조각」 절.
 * ② 「회차 둘 이상」으로 «뒤 회차가 그대로 있나»를 재는 시험을 먼저 세운다
 *    ⇒ ✅ calendar-byday-dtstart.test.ts(회차 수 ＋ 간격) · calendar-byday-ambiguous.test.ts
 *      ＋ ★ 이 판이 ⭐ 「날짜 집합」으로 재는 통을 더한다(위 ⚠ 때문에 수만으로는 모자라다).
 * ③ 사용자 결정을 새로 받는다  ⇒ ✅ 2026-10-06 사용자 결정(관제 중계)
 * ```
 *
 * ## ⭐⭐ 「BY* 조각」 — 되살림이 «새로 여는» 길. 사용자 결정 2026-10-06
 *
 * 되살림이 「수정 화면에서 날짜를 바꾸고 ‹전체 일정›/‹이후 모두›를 고르기」 길을 ★ 열었다
 * (종전엔 `['this']` 하나라 도달 불가였다 — 드래그는 막혀 있으므로 그쪽 길도 닫혀 있다).
 * ⇒ ⛔ 그런데 **우리가 밀지 못하는 RRULE 조각**이 있는 반복은 그 길에서 «조용히 틀린다».
 *
 * ### ★ 제품 함수로 잰 값 (delta ＝ 하루 뒤로 · 날짜 «집합»으로 맞댐)
 * ```
 * 대조군 BYDAY=MO            잃은 0 · 규칙 MO→TU                          ⇒ 정상(ⓐ)
 * 대조군 BYDAY=MO,WE         ⇒ ★ 400 으로 «막는다»                        ⇒ 정상(ⓕ)
 * ⛔ BYMONTHDAY=15           잃은 4 · 옮긴 날에 «아무것도 없음» · 한 달 미끄러짐
 * ⛔ BYDAY=MO;BYSETPOS=1     잃은 1 — ⚠ ★ «가장 조용하다»: BYDAY 가 MO→TU 로 다시 써져(ⓐ 가 돈다)
 *                            대체로 맞아 보이는데 ⇒ 12월만 틀린다(12/07 의 하루 뒤가 «12/01»).
 *                            ⇒ ★ 「첫 N요일」은 요일을 바꾸면 날짜가 최대 6일 점프한다
 * ⛔ BYMONTHDAY=31,-1;BYSETPOS=1(말일)  잃은 4 · 한 달 미끄러짐
 * ⛔⛔ BYMONTH=10;BYMONTHDAY=15(연간)  회차 1 → ★ «0» — 일정이 화면에서 통째로 사라진다
 * ```
 * ⇒ ★ 즉 네 꼴 모두 **ⓑ(통과하는데 회차가 틀리다)** 다 — ⛔ 400 으로 막지 «않는다».
 *
 * ### ⇒ ⭐ 사용자 결정(2026-10-06) ＝ 「권고」 ⇒ **ⓐ 관문만. 가드는 «지금» 세우지 않는다**
 * ```
 * 까닭  ★ 지금 그 조각을 «만들 수 있는 길이 0» 이다 ⇒ 운영에 그런 행이 있을 수 없다
 *       (buildRecurrenceRule 이 내는 조각 다섯 · 날 RRULE 입구 0 · main 이력에 만든 커밋 0건)
 * 관문  ⇒ 30차 배포 «앞»에 운영 조회를 돌린다(배포를 멈출 수 있는 자리여야 한다)
 * ```
 * ⇒ ⇒ ★ 그래서 가드를 「미루는」 것이 아니라 ⭐ **그 조각이 들어오는 판에 «묶었다»** —
 *   그 만료 조건은 `recurrence-scope-canon-gap.guard.test.ts` 의
 *   「BY* 조각을 «만들 수 있게» 되는 날」 통이 ★ 기계로 잡는다(해시가 아니라 «능력»을 잰다).
 * ⚠ 그리고 ★ 갈래가 **둘**이다 — ⛔ 하나만 읽으면 엉뚱한 쪽으로 간다:
 * ```
 * 조회가 0      ⇒ 위 만료 조건 통이 빨개지는 날 가드를 «함께» 세운다
 * 조회가 0 아님 ⇒ ⛔ 그 행은 «우리가 만든 것이 아니다» ⇒ ★ 가드보다 «그 길»을 먼저 찾는다
 *               (가드는 들어오는 것을 못 막는다 — 이미 있는 행을 안 망가뜨리게만 한다)
 * ```
 *
 * ## ⛔⛔ 드래그는 «켜지 않았다» — 별도 결정 사안
 *
 * **2026-10-05 사용자가 「b 드래그를 못하도록하자」로 «명시적으로» 막았다**
 * (`dragPolicy.isDragBlocked`). ⇒ ★ 이번 승인은 **「대화상자의 ‹전체 일정›을 되살리나」**에 대한
 * 것이고 ⛔ **드래그는 그 물음에 «없었다».** ⇒ 그래서 안 켰다. 켜려면 **사용자 결정을 새로 받는다.**
 *
 * ## ⇒ 이 좁힘을 «다시» 넣어야 할 날의 조건
 * 위 ⓐ~ⓖ 중 하나라도 되돌아가면 — 특히 ⓖ(`occurrenceAt` 이 와야 delta 로 민다)가 깨지면 —
 * 그때는 **좁히기 전에 ⓖ 를 위 ⓪① 꼴로 다시 재서** 무엇이 사라지는지 값으로 적는다.
 *
 * ━━━━━━━━━━━━━━━ 아래는 «지난 이력» — 왜 두 번 뺐었나 ━━━━━━━━━━━━━━━
 *
 * ① **2026-09-14 사용자 지시 — 수정·이동에서 '전체 일정'을 뺐다.**
 * 서버가 scope=all 수정을 "고른 회차의 시작 시각을
 * 시리즈 시작으로 삼는다" 로 처리해서, 제목만 바꿔도 그 회차보다 앞선 지난 회차들이 사라지고
 * 반복 횟수가 거기서부터 다시 세어진다(실측: 5회짜리에서 앞 2회 소멸 + 종료 2주 밀림).
 * 사용자는 제목만 고쳤다고 생각하는데 지난 기록이 없어지는 조용한 손실이라 선택지에서 뺐다.
 * ⇒ ★ 그것이 위 ⓖ 이고, ⭐ 이제 고쳐졌다(위에 값으로 적었다).
 *
 * 삭제는 그대로 셋 다 — scope=all 삭제는 의도대로 전부 지우고, 빼면 시리즈를 통째로 지울
 * 방법이 없어진다.
 *
 * ⛔⛔ **2026-10-05 — 종전 마지막 줄「서버가 "이동량만큼 시리즈를 민다" 로 고쳐지면 수정에도
 * 되살릴 수 있다」를 «닫는다». 그 줄이 이번 되살림의 근거였고, 되살린 판이 운영에서 터졌다.**
 *
 * 2026-09-14 뺌 → 2026-10-04 #2158 로 되살림(그 만료 조건이 「닫혔다」고 읽었다 — 서버에
 * `planSeriesShift` 가 들어왔으므로) → 다음 날 운영에서 사용자 실측 보고:
 *     「반복(월요일 2건) 중 앞의 것을 10/6(화)로 옮기고 «모든 일정»을 골랐더니
 *       뒤의 10/12 월요일 일정이 사라졌다」
 * ⇒ 사용자 결정(2026-10-05): 「반복일정은 이동이 안되도록하자」 ⇒ #2158 을 revert ＝ 이 파일.
 *
 * ## ★ 잰 것 — 전개 결과를 «값»으로 (제품 코드 `expandOccurrences`·`shiftRruleUntil` 를 불러서)
 * master 10/05 01:00Z · delta ＋1일 · 전개 범위 2026-09-01~12-31
 * ```
 *   FREQ=WEEKLY                  월요일 13회 → 화요일 13회   ✅ 회차 수 보존(10/12 는 10/13 으로)
 *   FREQ=WEEKLY;BYDAY=MO         13회 → 12회  ⛔ 10/05 가 «사라진다»
 *                                 그리고 옮긴 10/06 에는 «아무것도 안 생긴다»(첫 회차가 10/12)
 *   FREQ=WEEKLY;BYDAY=MO;COUNT=2 10/05·10/12 → 10/12·10/19
 *   FREQ=WEEKLY;UNTIL=…Z         UNTIL 도 같은 delta 로 밀려 회차 수 보존
 * ```
 * ⇒ 까닭: `planSeriesShift` 는 startAt·endAt·UNTIL·예외 키를 밀지만 **BYDAY 는 안 민다** ⇒
 *   DTSTART 만 화요일로 가고 규칙은 「매주 월요일」로 남아 첫 회차가 규칙 «밖»으로 나간다.
 * ⚠ **다만 이것은 운영 증상과 «다른» 모습이다** — BYDAY 판에서 없어지는 것은 «앞» 회차고
 *   «뒤» 회차(10/12)는 남는다. 내가 만들 수 있는 어떤 RRULE 꼴에서도 「10/06 은 있고 10/12 만
 *   없어지는」 모습은 **되풀이하지 못했다**(「못 잰 것」으로 적는다).
 * ⚠ 그리고 BYDAY 는 **앱(iOS·AOS)만 보낸다** — 웹은 `byday` 를 안 보낸다(보내는 곳 0건).
 *
 * ## ⇒ 세 번째 되살림을 막는 조건
 * 「서버가 시리즈를 민다」는 **더 이상 근거가 아니다** — 밀기는 이미 들어와 있고도 터졌다.
 * 되살리려면 ① BYDAY·BYMONTHDAY·BYSETPOS 를 어떻게 다룰지 정하고 ② **「회차 둘 이상」으로
 * «뒤 회차가 그대로 있나»를 재는 시험**을 먼저 세우고 ③ 사용자 결정을 새로 받는다.
 *
 * ⛔⛔ **2026-10-05(같은 날, 그 뒤) — 「이후 일정 모두」도 뺐다. 수정·이동은 이제 `['this']` 하나다.**
 *
 * 사용자 결정 원문: 「막습니다.」 ⇒ 까닭은 **같은 결함이 그 길에도 있다**는 것을 «둘이 따로» 쟀기 때문이다.
 * ```
 *   내가(코드로)   recurrenceRuleForFutureSplit 은 COUNT 만 고치고 BYDAY 를 그대로 물려준다
 *                  ⇒ 새 master 의 DTSTART 만 화요일로 가고 첫 회차는 그 다음 «월요일»이 된다
 *   관제가(실제 서버·실제 DB로)  id 104 「FREQ=WEEKLY;BYDAY=MO」 10/12 회차를 10/13 으로
 *                  PATCH ?scope=future ⇒ 200 · 회차 **12 → 11** · 10/12 가 없어지고 10/13 엔 아무것도 없다
 * ```
 * ⇒ ★ 그리고 관제가 치우다 **하나를 더** 찾았다: 「이후 모두」는 **새 master 를 만들어 시리즈를 둘로
 *   쪼갠다**(104 → 105). 그 뒤 `DELETE ?scope=all` 은 **부른 id 의 쪼가리만** 지워, 사용자에게는
 *   「지웠는데 아직 있다」로 보인다 — 「모든 일정」의 뜻이 «이 쪼가리 전부»가 되어 버린다.
 *
 * ## ⚠ 이 판이 «함께» 막는 것 — 재서 적는다
 * `['this']` 하나가 되면, **캘린더를 옮기는 수정**은 `disabled.this`(캘린더는 master 속성이라 회차
 * 하나만 못 옮긴다)와 겹쳐 **고를 수 있는 범위가 0** 이 된다 ⇒ 아래 `selectable.length === 0` 가
 * 확인을 막아 **취소만 남는다**. ⇒ 즉 **반복 일정의 캘린더를 바꾸는 길이 이 판에서 닫힌다.**
 * ⇒ 그것이 「조용히 막히는」 것이 아니라 **사유가 글자로 보이는** 자리라 그대로 둔다(사용자 결정 범위).
 *
 * ## ⚠ 창은 «그대로 띄운다»(ⓐ) — 안 띄우는 길(ⓑ)을 안 고른 까닭
 * 고를 것이 하나여도 **무엇이 일어날지 말해야** 한다. 그리고 그 말이 **이미 있다** —
 * `calendar.recurrence.hint_this_move` = 「고른 날짜 하루만 옮겨집니다. 나머지 반복은 그대로 남습니다.」
 * ⇒ 새로 지을 글이 **0** 이다. 창을 없애면 이 말이 사라진다.
 */
function scopesFor(_action: RecurrenceAction): EventScope[] {
  /*
   * ⭐ 2026-10-06 — 세 동작(삭제·수정·이동)이 ★ «같은 셋»을 보인다. 머리말의 ⓐ~ⓖ 가 근거다.
   * ⚠ 인자를 아직 받아 두는 까닭: 동작별로 다시 갈라야 할 날이 오면 ★ «부르는 쪽 6곳»이 아니라
   *   이 함수 «한 곳»만 고치면 되게. ⇒ 그 날의 조건도 머리말에 적어 두었다.
   */
  return SCOPES;
}

function labelOf(scope: EventScope): string {
  if (scope === 'this') return tCalendar('calendar.recurrence.scope_this');
  if (scope === 'future') return tCalendar('calendar.recurrence.scope_future');
  return tCalendar('calendar.recurrence.scope_all');
}

function hintOf(scope: EventScope, action: RecurrenceAction): string {
  if (action === 'delete') {
    if (scope === 'this') return tCalendar('calendar.recurrence.hint_this_delete');
    if (scope === 'future') return tCalendar('calendar.recurrence.hint_future_delete');
    return tCalendar('calendar.recurrence.hint_all_delete');
  }
  if (action === 'edit') {
    if (scope === 'this') return tCalendar('calendar.recurrence.hint_this_edit');
    if (scope === 'future') return tCalendar('calendar.recurrence.hint_future_edit');
    return tCalendar('calendar.recurrence.hint_all_edit');
  }
  if (scope === 'this') return tCalendar('calendar.recurrence.hint_this_move');
  if (scope === 'future') return tCalendar('calendar.recurrence.hint_future_move');
  return tCalendar('calendar.recurrence.hint_all_move');
}

/** 제목·본문·확인버튼 문구 — 동작별. */
function textsOf(action: RecurrenceAction, eventTitle: string) {
  if (action === 'delete') {
    return {
      title: tCalendar('calendar.recurrence.title_delete'),
      desc: tCalendar('calendar.recurrence.desc_delete', { title: eventTitle }),
      confirm: tCalendar('calendar.recurrence.confirm_delete'),
    };
  }
  if (action === 'edit') {
    return {
      title: tCalendar('calendar.recurrence.title_edit'),
      desc: tCalendar('calendar.recurrence.desc_edit', { title: eventTitle }),
      confirm: tCalendar('calendar.recurrence.confirm_edit'),
    };
  }
  return {
    title: tCalendar('calendar.recurrence.title_move'),
    desc: tCalendar('calendar.recurrence.desc_move', { title: eventTitle }),
    confirm: tCalendar('calendar.recurrence.confirm_move'),
  };
}

export function RecurrenceScopeHost() {
  useSyncExternalStore(subscribeCalendarLang, getCalendarLang, getCalendarLang);
  const queue = useScopeStore((s) => s.queue);
  const resolveTop = useScopeStore((s) => s.resolveTop);
  const top = queue[0];

  // 기본값은 언제나 'this' — 가장 덜 파괴적인 쪽이 기본이어야 한다. 이 화면이 생긴 이유가
  // "무심코 Enter 를 눌러 시리즈 전체가 날아가는 것" 을 막기 위해서다.
  const [picked, setPicked] = useState<EventScope>('this');
  useEffect(() => { setPicked('this'); }, [top?.id]);

  // 이 동작에서 보여줄 범위(⭐ 2026-10-06 부터 세 동작 모두 셋 — scopesFor 머리말 참조).
  const visible = useMemo(
    () => (top ? scopesFor(top.action) : SCOPES),
    [top?.id, top?.action],  // eslint-disable-line react-hooks/exhaustive-deps
  );
  const selectable = useMemo(
    () => visible.filter((s) => !top?.disabled?.[s]),
    [top?.id, top?.disabled, visible],  // eslint-disable-line react-hooks/exhaustive-deps
  );

  // 비활성 범위가 골라져 있으면 고를 수 있는 첫 범위로 옮긴다(확인이 400 으로 죽지 않게).
  useEffect(() => {
    if (!top) return;
    if (top.disabled?.[picked] && selectable[0]) setPicked(selectable[0]);
  }, [top, picked, selectable]);

  /*
   * ⭐ 2026-10-27 — 손으로 짠 창을 공용 `Modal`(＋ `CardRadio` · `Button`)로 옮겼다(사용자 승인 · 전후 화면 확인).
   *   ⇒ 라디오가 이제 «진짜» `<input type="radio">` 라, 위 옮김 효과가 돌기 «전» 한 번의 그림에서도
   *     막힌 범위에 점이 찍히지 않게 «그리는 값»은 여기서 한 번 더 거른다(고를 수 있는 첫 범위로).
   */
  const current: EventScope = top?.disabled?.[picked] ? (selectable[0] ?? picked) : picked;
  const optionRefs = useRef<Partial<Record<EventScope, HTMLInputElement | null>>>({});

  // 질문이 뜰 때마다 초점을 «기본 선택» 카드에 둔다. autoFocus 만으로는 모자란 자리가 있다 —
  // 줄 선 다음 질문으로 넘어갈 때 앞 창의 초점 트랩이 닫히며 초점을 바깥으로 «되돌려» 놓고, 새 트랩은
  // 그때 첫 단추(닫기 ✕)를 잡는다. 이 효과는 Modal 의 효과보다 «뒤»에 돈다(부모 효과는 자식 뒤).
  useEffect(() => {
    if (!top) return;
    const first = top.disabled?.this ? selectable[0] : 'this';
    if (first) optionRefs.current[first]?.focus();
  }, [top?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ⚠ Esc 는 여기서 «안» 받는다 — 공용 Modal 이 이미 받는다(useEscapeClose → onClose).
  //   둘 다 받으면 한 번의 Esc 에 resolveTop 이 «두 번» 돌아, 줄 서 있던 «다음» 질문까지 취소된다.
  const cancel = useCallback(() => resolveTop(null), [resolveTop]);

  useEffect(() => {
    if (!top) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Enter') {
        // 단추(취소 · 닫기 ✕ · 확인) 위의 Enter 는 «그 단추»의 일이다 — 가로채면 닫기 ✕ 에서 Enter 가 삭제가 된다.
        if (e.target instanceof HTMLButtonElement) return;
        e.preventDefault();
        // 고를 수 있는 범위가 0 이면 확인 단추처럼 Enter 도 막는다 — 막힌 범위를 그대로 보내지 않게.
        if (selectable.length === 0) return;
        resolveTop(current);
        return;
      }
      // 위/아래로 범위 이동 — 라디오 그룹의 통상 조작. 막힌 범위는 건너뛴다.
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const i = selectable.indexOf(current);
        if (i < 0) return;
        const next = e.key === 'ArrowDown'
          ? selectable[(i + 1) % selectable.length]
          : selectable[(i - 1 + selectable.length) % selectable.length];
        if (next) {
          setPicked(next);
          // 기본 동작(브라우저의 라디오 이동)을 막았으므로 초점도 «함께» 옮긴다 — 점과 초점이 갈라지지 않게.
          optionRefs.current[next]?.focus();
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [top, current, selectable, resolveTop]);

  if (!top) return null;

  const isDelete = top.action === 'delete';
  const { title, desc, confirm: confirmText } = textsOf(top.action, top.title);

  return createPortal(
    // 겹: 이 질문은 상세·수정 창(z 1000) «위»에서 뜬다 — 공용 scrim 의 z 를 이 겹에서만 올린다(RecurrenceScopeDialog.css).
    <div className="recur-scope-layer">
      <Modal
        key={top.id}
        size="sm"
        title={title}
        onClose={cancel}
        footer={(
          <ModalActions>
            <Button variant="secondary" onClick={cancel}>
              {tCalendar('calendar.recurrence.cancel')}
            </Button>
            <Button
              variant={isDelete ? 'danger' : 'primary'}
              // 고를 수 있는 범위가 하나도 없으면 확인을 막는다 — 2026-09-14 수정·이동에서 '전체 일정'을
              // 뺀 뒤 생긴 사각지대(예: 캘린더를 바꿔 '이 일정만'이 막히고, 뒤쪽 개별 수정 회차 때문에
              // '이후 일정 모두'도 막힌 경우). 예전에는 '전체 일정'이 받아 줬다. 취소만 열어 둔다.
              disabled={selectable.length === 0}
              onClick={() => resolveTop(current)}
            >
              {confirmText}
            </Button>
          </ModalActions>
        )}
      >
        <p className="recur-scope__desc">{desc}</p>

        <div className="recur-scope__options" role="radiogroup" aria-label={title}>
          {visible.map((scope) => {
            const reason = top.disabled?.[scope];
            const on = current === scope;
            const note = !reason ? top.notes?.[scope] : undefined;
            return (
              // 두 번 누르면 곧장 확인 — CardRadio 는 label 이라 dblclick 이 input 으로 안 넘어가서 감싸개가 받는다.
              <div
                key={scope}
                className="recur-scope__card"
                title={reason}
                onDoubleClick={() => { if (!reason) resolveTop(scope); }}
              >
                <CardRadio
                  ref={(el) => { optionRefs.current[scope] = el; }}
                  name={`recur-scope-${top.id}`}
                  value={scope}
                  checked={on}
                  disabled={!!reason}
                  onChange={() => setPicked(scope)}
                  autoFocus={on}
                  title={labelOf(scope)}
                  description={(
                    <>
                      {reason ?? top.hints?.[scope] ?? hintOf(scope, top.action)}
                      {note && <span className="recur-scope__optnote">{note}</span>}
                    </>
                  )}
                />
              </div>
            );
          })}
        </div>
      </Modal>
    </div>,
    document.body,
  );
}
